import React from 'react';
import { ChartBar, Clock, Award, Briefcase, Building, Globe, TrendingUp, AlertCircle, Loader2 } from 'lucide-react';
import { useResponsive } from '../../contexts/ResponsiveContext';
import { Button } from '../common/Button';
import { UserInterview } from '../../services/interviewService';

// Updated DateFilter interface
interface DateFilter {
  value: string;
  label: string;
  fromDate?: string;
  toDate?: string;
}

interface Interview {
  id: string;
  date: string;
  title: string;
  jobRole: string;
  company: string;
  language: string;
  duration: number;
  score: number;
}

interface AnalyticsTabProps {
  interviews: UserInterview[];
  dateFilters: DateFilter[];
  selectedDateFilter: string;
  onDateFilterChange: (filter: string) => void;
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
}

export const AnalyticsTab: React.FC<AnalyticsTabProps> = ({ 
  interviews, 
  dateFilters,
  selectedDateFilter,
  onDateFilterChange,
  isLoading,
  error,
  onRetry
}) => {
  const { isMobile: isMobileView } = useResponsive();
  
  // No client-side filtering anymore since it's done on the server
  const filteredInterviews = interviews;

  // Calculate statistics from filtered interviews
  const stats = React.useMemo(() => {
    // During initial loading, return default values
    if (isLoading || filteredInterviews.length === 0) {
      return {
        totalInterviews: 0,
        totalDuration: 0,
        averageScore: 0,
        bestScore: 0,
        worstScore: 0,
        improvementRate: 0
      };
    }
    
    // Sort interviews by date
    const sortedInterviews = [...filteredInterviews].sort((a, b) => 
      new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime()
    );
    
    const totalInterviews = filteredInterviews.length;
    const totalDuration = filteredInterviews.reduce((sum, interview) => sum + (interview.minutesUsed || 0), 0);
    const averageScore = Math.round(
      filteredInterviews.reduce((sum, interview) => sum + (interview.feedback?.overallScore || 0), 0) / totalInterviews
    );
    const bestScore = Math.max(...filteredInterviews.map(interview => interview.feedback?.overallScore || 0));
    const worstScore = Math.min(...filteredInterviews.map(interview => interview.feedback?.overallScore || 0));
    
    // Calculate improvement rate (comparing first and last interviews)
    const firstScore = sortedInterviews[0].feedback?.overallScore || 0;
    const lastScore = sortedInterviews[sortedInterviews.length - 1].feedback?.overallScore || 0;
    const improvementRate = lastScore - firstScore;
    
    return {
      totalInterviews,
      totalDuration,
      averageScore,
      bestScore,
      worstScore,
      improvementRate
    };
  }, [filteredInterviews, isLoading]);
  
  // Find most interviewed role, company and language from filtered interviews
  const roleCount = (!isLoading && filteredInterviews.length > 0) ? filteredInterviews.reduce((acc, interview) => {
    acc[interview?.interview?.role || ''] = (acc[interview?.interview?.role || ''] || 0) + 1;
    return acc;
  }, {} as Record<string, number>) : {};
  
  const companyCount = (!isLoading && filteredInterviews.length > 0) ? filteredInterviews.reduce((acc, interview) => {
    acc[interview?.interview?.company || ''] = (acc[interview?.interview?.company || ''] || 0) + 1;
    return acc;
  }, {} as Record<string, number>) : {};

  const languageCount = (!isLoading && filteredInterviews.length > 0) ? filteredInterviews.reduce((acc, interview) => {
    acc[interview.language || ''] = (acc[interview.language || ''] || 0) + 1;
    return acc;
  }, {} as Record<string, number>) : {};
  
  const mostInterviewedRole = stats.totalInterviews > 0 && Object.keys(roleCount).length > 0
    ? Object.entries(roleCount).sort((a, b) => b[1] - a[1])[0][0]
    : 'N/A';
    
  const mostInterviewedCompany = stats.totalInterviews > 0 && Object.keys(companyCount).length > 0
    ? Object.entries(companyCount).sort((a, b) => b[1] - a[1])[0][0]
    : 'N/A';

  const mostInterviewedLanguage = stats.totalInterviews > 0 && Object.keys(languageCount).length > 0
    ? Object.entries(languageCount).sort((a, b) => b[1] - a[1])[0][0]
    : 'N/A';

  // Show error state
  if (error) {
    return (
      <div className="space-y-8">
        <div className="bg-background-light/50 dark:bg-background-dark/50 backdrop-blur-sm rounded-xl border border-red-500/20 p-4 md:p-6">
          <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
            <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mb-4">
              <AlertCircle className="w-6 h-6 text-red-500" />
            </div>
            <h4 className="text-lg font-medium text-text-primary-light dark:text-text-primary-dark mb-2">
              Failed to Load Analytics
            </h4>
            <p className="text-text-secondary-light dark:text-text-secondary-dark text-sm max-w-sm mb-4">
              {error}
            </p>
            <Button
              variant="secondary"
              onClick={onRetry}
              className="text-sm"
            >
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Analytics Overview */}
      <div className="bg-background-light/50 dark:bg-background-dark/50 backdrop-blur-sm rounded-xl border border-border-light dark:border-border-dark p-4 md:p-6 transition-all duration-300 hover:border-primary-light/30 dark:hover:border-primary-dark/30">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            {!isMobileView && (
              <div className="p-2 rounded-full bg-primary-light/10 dark:bg-primary-dark/10 mr-3">
                <ChartBar className="w-5 h-5 text-primary-light dark:text-primary-dark" />
              </div>
            )}
            <h3 className="text-lg font-medium text-text-primary-light dark:text-text-primary-dark">
              Performance Analytics
            </h3>
          </div>
          
          {/* Date Filter Dropdown - with loading state */}
          <div className="relative">
            <select
              value={selectedDateFilter}
              onChange={(e) => onDateFilterChange(e.target.value)}
              disabled={isLoading}
              className={`appearance-none bg-background-light dark:bg-background-dark border border-border-light dark:border-border-dark rounded-lg px-4 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark ${
                isLoading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {dateFilters.map(filter => (
                <option key={filter.value} value={filter.value}>
                  {filter.label}
                </option>
              ))}
            </select>
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
              {isLoading ? (
                <Loader2 className="w-4 h-4 text-primary-light dark:text-primary-dark animate-spin" />
              ) : (
                <svg className="w-4 h-4 text-text-secondary-light dark:text-text-secondary-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              )}
            </div>
          </div>
        </div>
        
        {/* Content with loading state */}
        {isLoading ? (
          <div className="flex flex-col justify-center items-center py-12">
           <Loader2 className="w-12 h-12 text-primary-light dark:text-primary-dark animate-spin mb-4" />
            <p className="text-base text-text-secondary-light dark:text-text-secondary-dark">Loading analytics...</p>
          </div>
        ) : stats.totalInterviews > 0 ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              {/* Total Interviews */}
              <div className="bg-surface-light dark:bg-surface-dark p-4 rounded-lg border border-border-light dark:border-border-dark">
                <div className="flex items-center mb-2">
                  <div className="p-2 rounded-full bg-primary-light/10 dark:bg-primary-dark/10 mr-2">
                    <TrendingUp size={16} className="text-primary-light dark:text-primary-dark" />
                  </div>
                  <h4 className="text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark">
                    Total Interviews
                  </h4>
                </div>
                <p className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">
                  {stats.totalInterviews}
                </p>
              </div>
              
              {/* Total Duration */}
              <div className="bg-surface-light dark:bg-surface-dark p-4 rounded-lg border border-border-light dark:border-border-dark">
                <div className="flex items-center mb-2">
                  <div className="p-2 rounded-full bg-primary-light/10 dark:bg-primary-dark/10 mr-2">
                    <Clock size={16} className="text-primary-light dark:text-primary-dark" />
                  </div>
                  <h4 className="text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark">
                    Total Duration (Interviewed)
                  </h4>
                </div>
                <p className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">
                  {Math.round(stats.totalDuration * 10) / 10} mins
                </p>
              </div>
              
              {/* Average Score */}
              <div className="bg-surface-light dark:bg-surface-dark p-4 rounded-lg border border-border-light dark:border-border-dark">
                <div className="flex items-center mb-2">
                  <div className="p-2 rounded-full bg-primary-light/10 dark:bg-primary-dark/10 mr-2">
                    <Award size={16} className="text-primary-light dark:text-primary-dark" />
                  </div>
                  <h4 className="text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark">
                    Average Score
                  </h4>
                </div>
                <p className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">
                  {stats.averageScore}%
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Best Score */}
              <div className="bg-surface-light dark:bg-surface-dark p-4 rounded-lg border border-border-light dark:border-border-dark">
                <div className="flex items-center mb-2">
                  <div className="p-2 rounded-full bg-green-500/10 dark:bg-green-400/10 mr-2">
                    <TrendingUp size={16} className="text-green-500 dark:text-green-400" />
                  </div>
                  <h4 className="text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark">
                    Best Score
                  </h4>
                </div>
                <p className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">
                  {stats.bestScore}%
                </p>
              </div>
              
              {/* Worst Score */}
              <div className="bg-surface-light dark:bg-surface-dark p-4 rounded-lg border border-border-light dark:border-border-dark">
                <div className="flex items-center mb-2">
                  <div className="p-2 rounded-full bg-red-500/10 dark:bg-red-400/10 mr-2">
                    <TrendingUp size={16} className="text-red-500 dark:text-red-400 transform rotate-180" />
                  </div>
                  <h4 className="text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark">
                    Lowest Score
                  </h4>
                </div>
                <p className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark">
                  {stats.worstScore}%
                </p>
              </div>
              
              {/* Improvement Rate */}
              <div className="bg-surface-light dark:bg-surface-dark p-4 rounded-lg border border-border-light dark:border-border-dark">
                <div className="flex items-center mb-2">
                  <div className={`p-2 rounded-full ${stats.improvementRate >= 0 ? 'bg-green-500/10 dark:bg-green-400/10' : 'bg-red-500/10 dark:bg-red-400/10'} mr-2`}>
                    <TrendingUp 
                      size={16} 
                      className={`${stats.improvementRate >= 0 ? 'text-green-500 dark:text-green-400' : 'text-red-500 dark:text-red-400 transform rotate-180'}`} 
                    />
                  </div>
                  <h4 className="text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark">
                    Improvement
                  </h4>
                </div>
                <p className={`text-2xl font-bold ${stats.improvementRate > 0 ? 'text-green-500 dark:text-green-400' : stats.improvementRate === 0 ? 'text-text-primary-light dark:text-text-primary-dark' : 'text-red-500 dark:text-red-400'}`}>
                  {stats.improvementRate > 0 ? '+' : stats.improvementRate === 0 ? '' : ''}{stats.improvementRate}%
                </p>
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
            <div className="w-12 h-12 rounded-full bg-primary-light/10 dark:bg-primary-dark/10 flex items-center justify-center mb-4">
              <AlertCircle className="w-6 h-6 text-primary-light dark:text-primary-dark" />
            </div>
            <h4 className="text-lg font-medium text-text-primary-light dark:text-text-primary-dark mb-2 font-heading font-semibold">
              No Interview Details
            </h4>
            <p className="text-text-secondary-light dark:text-text-secondary-dark text-sm max-w-sm">
              Complete at least one interview to see analytics and insights about your performance.
            </p>
          </div>
        )}
      </div>

      {/* Most Interviewed Stats */}
      {!isLoading && (
        <div className="mb-10 bg-background-light/50 dark:bg-background-dark/50 backdrop-blur-sm rounded-xl border border-border-light dark:border-border-dark p-5 transition-all duration-300 hover:border-primary-light/30 dark:hover:border-primary-dark/30 relative">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Most Interviewed Role */}
          <div className="flex">
            <div className="p-2 h-fit rounded-full bg-primary-light/10 dark:bg-primary-dark/10 mr-4">
              <Briefcase className="w-5 h-5 text-primary-light dark:text-primary-dark" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark mb-2">
                Most Interviewed Role
              </h3>
              <div className="text-xl font-bold text-text-primary-light dark:text-text-primary-dark">
                {mostInterviewedRole}
              </div>
            </div>
          </div>

          {/* Most Interviewed Company */}
          <div className="flex">
            <div className="p-2 h-fit rounded-full bg-primary-light/10 dark:bg-primary-dark/10 mr-4">
              <Building className="w-5 h-5 text-primary-light dark:text-primary-dark" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark mb-2">
                Most Interviewed Company
              </h3>
              <div className="text-xl font-bold text-text-primary-light dark:text-text-primary-dark">
                {mostInterviewedCompany || 'No company'}
              </div>
            </div>
          </div>
          
          {/* Most Interviewed Language */}
          <div className="flex">
            <div className="p-2 h-fit rounded-full bg-primary-light/10 dark:bg-primary-dark/10 mr-4">
              <Globe className="w-5 h-5 text-primary-light dark:text-primary-dark" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark mb-2">
                Most Interviewed Language
              </h3>
              <div className="text-xl font-bold text-text-primary-light dark:text-text-primary-dark">
                {mostInterviewedLanguage || 'English'}
              </div>
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}; 